import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, TextField, IconButton, Avatar, Switch,
  FormControlLabel, CircularProgress,
} from '@mui/material';
import { Add, DeleteOutline, PhotoCamera } from '@mui/icons-material';
import { SectionTitle, useToast } from './ui';
import { radius } from '../theme';
import compressImage from '../utils/imageCompress';

const emptyComponent = () => ({ name: '', kcal_per_100g: '', protein_g: '', fat_g: '', carbs_g: '' });

const sanitizeDecimal = (v) => {
  let s = v.replace(/[^0-9.,]/g, '').replace(',', '.');
  const parts = s.split('.');
  if (parts.length > 2) s = parts[0] + '.' + parts.slice(1).join('');
  return s;
};

// Anlegen/Bearbeiten eines Gerichts aus mehreren Zutaten (Name + kcal/100g,
// Protein/Fett/Carbs optional), mit optionalem Foto und Community-Freigabe.
export default function DishFormDialog({ open, onClose, dish, onSaved }) {
  const isEdit = Boolean(dish);
  const [name,       setName]       = useState('');
  const [isPublic,   setIsPublic]   = useState(false);
  const [components, setComponents] = useState([emptyComponent()]);
  const [photoFile,    setPhotoFile]    = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (!open) return;
    if (dish) {
      setName(dish.name || '');
      setIsPublic(Boolean(dish.is_public));
      setComponents(
        dish.components?.length
          ? dish.components.map((c) => ({
              name: c.name || '',
              kcal_per_100g: String(c.kcal_per_100g ?? ''),
              protein_g: String(c.protein_g ?? 0),
              fat_g: String(c.fat_g ?? 0),
              carbs_g: String(c.carbs_g ?? 0),
            }))
          : [emptyComponent()]
      );
      setPhotoPreview(dish.photo_url || null);
    } else {
      setName('');
      setIsPublic(false);
      setComponents([emptyComponent()]);
      setPhotoPreview(null);
    }
    setPhotoFile(null);
    setCompressing(false);
  }, [open, dish]);

  const updateComponent = (idx, field, value) =>
    setComponents((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));

  const addComponent = () => setComponents((prev) => [...prev, emptyComponent()]);

  const removeComponent = (idx) =>
    setComponents((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    // Der Input wird zurückgesetzt, damit dasselbe Foto erneut gewählt werden kann.
    e.target.value = '';
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    setCompressing(true);
    try {
      setPhotoFile(await compressImage(file));
    } finally {
      setCompressing(false);
    }
  };

  const isValid = name.trim().length > 0 && components.every(
    (c) => c.name.trim().length > 0 && c.kcal_per_100g !== '' && Number.isFinite(Number(c.kcal_per_100g))
  );

  const handleSubmit = async () => {
    const token = localStorage.getItem('token');
    if (!token || !isValid) return;

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('is_public', String(isPublic));
      formData.append('components', JSON.stringify(components.map((c) => ({
        name: c.name.trim(),
        kcal_per_100g: Number(c.kcal_per_100g),
        protein_g: Number(c.protein_g) || 0,
        fat_g: Number(c.fat_g) || 0,
        carbs_g: Number(c.carbs_g) || 0,
      }))));
      if (photoFile) formData.append('photo', photoFile);

      const url    = isEdit ? `/api/dishes/${dish.id}` : '/api/dishes';
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (!res.ok) {
        if (res.status === 413) {
          throw new Error('Das Foto ist zu groß. Bitte ein kleineres Foto verwenden.');
        }
        let msg = 'Fehler beim Speichern des Gerichts.';
        try {
          const body = await res.json();
          if (body?.error) msg = body.error;
        } catch { /* keine JSON-Antwort (z. B. HTML-Fehlerseite) */ }
        throw new Error(msg);
      }
      onSaved();
    } catch (err) {
      showToast(err?.message || 'Fehler beim Speichern des Gerichts.', { severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEdit ? 'Gericht bearbeiten' : 'Neues Gericht'}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>

          {/* Foto */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar variant="rounded" src={photoPreview || undefined} sx={{ width: 64, height: 64, bgcolor: 'action.hover' }}>
              <PhotoCamera sx={{ color: 'text.disabled' }} />
            </Avatar>
            <Button
              component="label" variant="outlined" disabled={compressing}
              startIcon={compressing ? <CircularProgress size={18} /> : <PhotoCamera />}
            >
              {compressing ? 'Foto wird verarbeitet…' : (photoPreview ? 'Foto ändern' : 'Foto auswählen')}
              <input hidden type="file" accept="image/*" onChange={handlePhotoChange} />
            </Button>
          </Box>

          <TextField
            fullWidth
            label="Name des Gerichts"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onFocus={(e) => e.target.select()}
          />

          <FormControlLabel
            control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
            label="Für die Community freigeben"
          />

          <SectionTitle sx={{ mt: 0 }}>Zutaten</SectionTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {components.map((c, idx) => (
              <Box key={idx} sx={{ border: 1, borderColor: 'divider', borderRadius: radius.lg, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth size="small" label="Zutat" value={c.name}
                    onChange={(e) => updateComponent(idx, 'name', e.target.value)}
                    onFocus={(e) => e.target.select()}
                  />
                  <IconButton
                    size="small" onClick={() => removeComponent(idx)}
                    disabled={components.length === 1}
                    sx={{ color: 'text.disabled' }}
                  >
                    <DeleteOutline sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <TextField
                    size="small" label="kcal / 100g" value={c.kcal_per_100g}
                    onChange={(e) => updateComponent(idx, 'kcal_per_100g', sanitizeDecimal(e.target.value))}
                    onFocus={(e) => e.target.select()}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                  />
                  <TextField
                    size="small" label="Protein g (optional)" value={c.protein_g}
                    onChange={(e) => updateComponent(idx, 'protein_g', sanitizeDecimal(e.target.value))}
                    onFocus={(e) => e.target.select()}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                  />
                  <TextField
                    size="small" label="Fett g (optional)" value={c.fat_g}
                    onChange={(e) => updateComponent(idx, 'fat_g', sanitizeDecimal(e.target.value))}
                    onFocus={(e) => e.target.select()}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                  />
                  <TextField
                    size="small" label="Carbs g (optional)" value={c.carbs_g}
                    onChange={(e) => updateComponent(idx, 'carbs_g', sanitizeDecimal(e.target.value))}
                    onFocus={(e) => e.target.select()}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
          <Button startIcon={<Add />} onClick={addComponent} sx={{ alignSelf: 'flex-start' }}>
            Zutat hinzufügen
          </Button>

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ flex: 1 }} disabled={saving}>
          Abbrechen
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isValid || saving || compressing} sx={{ flex: 1 }}>
          {saving ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Speichern' : 'Anlegen')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
